import { useEffect, useLayoutEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { useBreakpoint, useLocal } from "~/hooks";
import type { FloatingMenuProps } from "./interfaces";
import { updateContentHeight } from "./utils";

const DEFAULT_NAVIGATION_LOCATION = {
    left: "4px",
    top: "50%",
    horizontal: false,
    hidden: false,
};

// @TODO: refactor, make more readable
const useFloatingMenu = ({
    id,
    ghost,
    defaultPosition = DEFAULT_NAVIGATION_LOCATION,
}: FloatingMenuProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const lastMouse = useRef({ x: 0, y: 0 });
    const isMoving = useSignal(false);
    const location = useLocal(
        `v1.1_floating_menu_${id}`,
        defaultPosition,
        Boolean(ghost),
    );
    const isReverse = useSignal(false);
    const menuHeight = useSignal(0);
    const bpMd = useBreakpoint("md");

    const handleMouseDown = (e: MouseEvent) => {
        if (!ref.current) {
            return;
        }

        lastMouse.current = {
            x: e.clientX,
            y: e.clientY,
        };

        isMoving.value = true;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
    };

    const handleMouseLeave = () => {
        if (isMoving.value) {
            return;
        }

        checkReverse();
    };

    const checkReverse = () => {
        if (ref.current) {
            const { left } = ref.current.getBoundingClientRect();
            const horizontal = location.peek().horizontal;

            if (!horizontal) isReverse.value = left > window.innerWidth / 2;
        }
    };

    const handleMouseEnter = () => {
        if (isMoving.value) {
            return;
        }
        checkReverse();
    };

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const initialLocation = location.peek();

        ref.current.style.left = initialLocation.left ?? "";
        ref.current.style.top = initialLocation.top ?? "";
        ref.current.style.right = initialLocation.right ?? "";
        ref.current.style.bottom = initialLocation.bottom ?? "";
    }, [location]);

    useEffect(() => {
        if (ghost) {
            return;
        }

        return () => updateContentHeight(false);
    }, [ghost]);

    useLayoutEffect(() => {
        if (location.peek().hidden) {
            const tabs = document.querySelector<HTMLElement>(
                "[data-floating-tabs]",
            );
            const trigger = document.querySelector<HTMLElement>(
                "[data-floating-trigger]",
            );

            if (tabs) {
                tabs.style.display = "block";
                tabs
                    .querySelector<HTMLElement>("[data-floating-content]")
                    ?.classList.remove("grayscale");
                updateContentHeight(true);
            }

            if (!trigger) {
                return;
            }

            const { left, top } = trigger.getBoundingClientRect();

            location.value = {
                ...location.peek(),
                left: `${left}px`,
                top: `${top}px`,
            };
        }
    }, [location]);

    useLayoutEffect(() => {
        const trigger = document.querySelector<HTMLElement>(
            "[data-floating-trigger]",
        );

        if (!trigger) {
            return;
        }

        trigger.addEventListener("mousedown", ({ clientX, clientY }) => {
            if (isMoving.peek()) {
                return;
            }

            isMoving.value = true;
            location.value = {
                left: `${clientX}px`,
                top: `${clientY}px`,
                horizontal: true,
                hidden: false,
            };
            lastMouse.current = {
                x: clientX,
                y: clientY,
            };
            document.body.style.userSelect = "none";
            document.body.style.cursor = "grabbing";
        });
    }, [isMoving, location]);

    useEffect(() => {
        if (ghost) {
            return;
        }

        const toggleStaticTabs = (visible: boolean, tabs: HTMLElement) => {
            if (visible) {
                tabs.style.display = "block";
            } else {
                tabs.style.display = "";
                tabs.classList.add("hidden");
            }

            updateContentHeight(visible);
            window.dispatchEvent(
                new CustomEvent("floating-menu.static-tabs", {
                    detail: { id, visible },
                }),
            );
        };

        const checkForGhosts = () => {
            if (!ref.current) {
                return;
            }

            const box = ref.current.getBoundingClientRect();

            document
                .querySelectorAll<HTMLElement>(
                    "[data-floating-menu=left], [data-floating-menu=bottom], [data-floating-menu=right], [data-floating-tabs]",
                )
                .forEach((ghost) => {
                    const ghostBox = ghost.getBoundingClientRect();

                    if (
                        box.right > ghostBox.left &&
                        box.left < ghostBox.right &&
                        box.bottom > ghostBox.top &&
                        box.top < ghostBox.bottom
                    ) {
                        ghost
                            .querySelector<HTMLElement>(
                                "[data-floating-content]",
                            )
                            ?.classList.remove("grayscale");
                        ghost.setAttribute("data-floating-active", "true");
                    } else {
                        ghost
                            .querySelector<HTMLElement>(
                                "[data-floating-content]",
                            )
                            ?.classList.add("grayscale");
                        ghost.removeAttribute("data-floating-active");
                    }
                });
        };

        const handleMouseUp = () => {
            if (!isMoving.value) {
                return;
            }

            isMoving.value = false;
            document.body.style.userSelect = "auto";
            document.body.style.cursor = "auto";

            const active = document.querySelector<HTMLElement>(
                "[data-floating-active]",
            );
            const tabs = document.querySelector<HTMLElement>(
                "[data-floating-tabs]",
            );

            if (tabs && tabs !== active) {
                toggleStaticTabs(false, tabs);
            }

            if (!active || !ref.current) {
                checkReverse();
                return;
            }

            if (tabs === active) {
                location.value = { ...location.value, hidden: true };
                toggleStaticTabs(true, tabs);
                return;
            }

            const left = active.dataset.floatingLeft ?? "";
            const top = active.dataset.floatingTop ?? "";
            const right = active.dataset.floatingRight ?? "";
            const bottom = active.dataset.floatingBottom ?? "";
            const horizontal = active.dataset.floatingHorizontal === "true";

            location.value = { left, top, right, bottom, horizontal };
            ref.current.style.left = left;
            ref.current.style.top = top;
            ref.current.style.right = right;
            ref.current.style.bottom = bottom;
            active.removeAttribute("data-floating-active");
            checkReverse();
        };

        const handleMouseMove = (event: MouseEvent) => {
            if (!ref.current || !isMoving.value) {
                return;
            }

            const padding = 4;
            const box = ref.current.getBoundingClientRect();
            const { innerWidth, innerHeight } = window;
            const { x: mx, y: my } = lastMouse.current;
            const dx = event.clientX - mx;
            const dy = event.clientY - my;

            const ux = Math.min(
                Math.max(box.left + dx, padding),
                innerWidth - box.width - padding,
            );
            const uy = Math.min(
                Math.max(box.top + dy, padding),
                innerHeight - box.height - padding,
            );

            const left = `${ux}px`;
            const top = `${uy}px`;

            ref.current.style.right = "";
            ref.current.style.bottom = "";
            ref.current.style.left = left;
            ref.current.style.top = top;
            location.value = {
                left,
                top,
                horizontal: location.value.horizontal,
            };
            lastMouse.current = {
                x: event.clientX,
                y: event.clientY,
            };

            checkForGhosts();
        };

        window.addEventListener("mouseup", handleMouseUp);
        window.addEventListener("mousemove", handleMouseMove);

        return () => {
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("mousemove", handleMouseMove);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMoving, location, ghost]);

    const isHorizontal = Boolean(location.peek().horizontal);
    const isHidden = Boolean(location.value.hidden || !bpMd.value);

    useEffect(() => {
        if (ref.current) {
            menuHeight.value = ref.current.clientHeight;
        }
    }, [isHorizontal, menuHeight]);

    return {
        ref,
        location,
        menuHeight,
        isHorizontal,
        isMoving,
        isReverse,
        isHidden,
        handleMouseDown: ghost ? undefined : handleMouseDown,
        handleMouseEnter: ghost ? undefined : handleMouseEnter,
        handleMouseLeave: ghost ? undefined : handleMouseLeave,
    };
};

export default useFloatingMenu;
